// ============================================================================
// 💰 BUDGETS STORE
//    💡 Store para la gestión de presupuestos:
//       - Carga de vista desde JSON local
//       - Búsqueda de presupuestos vía API
//       - Gestión de estados de carga y error
//       - Compatible con interfaz ISearchStore para integración con búsquedas
// ============================================================================

import { defineStore } from "pinia";
import { getEndpoints } from "@/services/apiServices/endpoints";
import { fetchPostData, fetchGetData } from "@/services/apiServices/http";
import type { ISearchStore } from "@/interfaces/stores/ISearchStore";

export const useBudgetsStore = defineStore("budgetsStore", {
  // 📦 Estado reactivo
  state: () => ({
    budgetsViewData: {} as any, // Datos visuales para tarjetas resumen de presupuestos
    budgets: {} as any, // Resultado de la búsqueda de presupuestos
    loading: false as boolean | undefined, // Indicador de carga (usado en botón de búsqueda)
    error: null as Error | null, // Error global de la store
  }),

  // 🔍 Getters
  getters: {
    getBudgetsView: (state) => state.budgetsViewData, // Tarjetas visuales (p.ej. presupuestos totales)
    getBudgets: (state) => state.budgets, // Resultado de búsqueda
    getLoading: (state) => state.loading, // Estado de carga
  },

  // ⚙️ Acciones principales
  actions: {
    /**
     * 🟢 Carga datos de vista desde un JSON local
     *    Se utiliza para mostrar datos visuales resumen (tarjetas, gráficas, etc.)
     */
    async budgetsView() {
      try {
        const data = await fetchGetData<any>(
          "/assets/data/budgets/totalBudgetsCard.json",
          ""
        );
        this.budgetsViewData = data[0];
        console.log("Presupuestos (vista):", data[0]);
      } catch (error) {
        console.error("Error cargando budgetsViewData:", error);
        this.error = error as Error;
      }
    },

    /**
     * 🔍 Realiza una búsqueda de presupuestos mediante POST
     * @param query - Cuerpo del filtro de búsqueda
     * @param token - Token de autenticación
     */
    async search(query: any, token: string) {
      this.loading = true;
      this.error = null;

      try {
        const URL = getEndpoints().budgetsStore;
        const data = await fetchPostData<any>(URL, token, query);
        this.budgets = data;
        console.log("Presupuestos cargados:", data);
      } catch (error) {
        console.error("Error en la búsqueda de presupuestos:", error);
        this.error = error as Error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * 🔁 Restablece el flag de carga a undefined
     *    Útil para dejar el botón de búsqueda en estado neutral tras recarga
     */
    changeFlag() {
      this.loading = undefined;
    },
  },
});

// 📤 Export compatible con ISearchStore (para buscadores reutilizables)
export const budgetsStore = useBudgetsStore as unknown as ISearchStore;
